// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Tooltip, { TooltipPresentationProps } from "@guestsupply/MobiusOverrides/Tooltip/Tooltip";
import Minus from "@guestsupply/XDL/Icons/Minus";
import Plus from "@guestsupply/XDL/Icons/Plus";
import QuestionTooltip from "@guestsupply/XDL/Icons/QuestionTooltip";
import { mergeWithCss, xClassNames, xMask } from "@guestsupply/XDL/Utilities";
import xDefaultRules from "@guestsupply/XDL/Utilities/xValidate/xDefaultRules";
import IsNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import { Clickable, Typography } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps extends Omit<TextFieldProps, "css"> {
    onDecreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    onIncreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    showActionButton?: boolean;
    extendedStyles?: QtyFieldStyles;
    allowZero?: boolean;
    editable?: boolean;
    showLabel?: boolean;
    showMultipleSaleQty?: boolean;
    min?: number | string;
    roundBy?: number;
}

type Props = OwnProps;

export interface QtyFieldStyles {
    container?: InjectableCss;
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    countButton?: ButtonPresentationProps;
    increaseButton?: ButtonPresentationProps;
    decreaseButton?: ButtonPresentationProps;
    countIcon?: IconPresentationProps;
    qtyLabel?: TypographyProps;
    valueText?: TypographyProps;
    controlsWrapper?: InjectableCss;
    qtyClickable?: ClickablePresentationProps;
    QuestionIconStyles?: IconProps;
    multipleSaleQtyWrapper?: InjectableCss;
    printQtyLabel?: TypographyProps;
    printValueText?: TypographyProps;
    multipleSaleQtyToolTip?: TooltipPresentationProps;
}

export const qtyFieldStyles: QtyFieldStyles = {};

const enterKey = 13;

const QtyField: FC<Props> = ({
    extendedStyles,
    onIncreaseChangeHandler,
    onDecreaseChangeHandler,
    disabled: disabledProps = false,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    label: labelOverride,
    showActionButton = true,
    allowZero = false,
    editable = true,
    showLabel = true,
    showMultipleSaleQty = false,
    roundBy = 1,
    min = 0,
    ...qtyFieldsProps
}) => {
    const [styles] = useState(() => mergeWithCss(qtyFieldStyles, extendedStyles));
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);

    let minQty = 0;
    let maxQty = 9999;
    xDefaultRules["qty"].split("|").forEach(item => {
        const [attr, val] = item.split(":");
        if (attr === "min") {
            minQty = Math.max(Number(min), +val);
        }
        if (attr === "max") {
            maxQty = +val;
        }
    }, {});

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.target.value = xMask.qty(event.target.value);
        onChangeTextField?.(event);
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        if (event?.currentTarget) {
            event.currentTarget.value = xMask.qty(event.currentTarget.value);
            onChangeHandler(event);
        }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - roundBy >= 0
                ? Number(currentValue) - roundBy
                : undefined;

        if (allowZero && nextValue === undefined) {
            return;
            // eslint-disable-next-line no-else-return
        } else if (!allowZero && !nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onDecreaseChangeHandler) {
            event = onDecreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const onIncreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + roundBy <= maxQty
                ? Number(currentValue) + roundBy
                : undefined;

        if (!nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onIncreaseChangeHandler) {
            event = onIncreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };
    return (
        <StyledWrapper {...styles.container}>
            {editable ? (
                <>
                    {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                    {/* Xng-Change: add print label and value */}
                    <Typography {...styles.printQtyLabel}>{labelOverride || translate("QTY")}</Typography>
                    <Typography {...styles.printValueText}>{currentValue}</Typography>
                    <StyledWrapper
                        {...styles.wrapper}
                        data-test-selector="quantity-field"
                        className={xClassNames.append([
                            disabled && "qty-disabled-wrapper",
                            showActionButton && "show-action-button-wrapper",
                        ])}
                    >
                        <TextField
                            type="qty"
                            disabled={disabled}
                            value={currentValue}
                            onChange={onChangeHandler}
                            onBlur={onBlurHandler}
                            onClick={onClickHandler}
                            onKeyDown={onKeyDownHandler}
                            className={xClassNames.append([
                                disabled && "qty-disabled",
                                showActionButton && "show-action-button",
                            ])}
                            {...styles.editableQuantityTextField}
                            {...qtyFieldsProps}
                        />
                        {showActionButton && (
                            <StyledWrapper {...styles.controlsWrapper}>
                                <Clickable
                                    {...styles.qtyClickable}
                                    className={`plus ${disabled ? "disabled" : ""}`}
                                    disabled={disabled || Number(currentValue) === maxQty}
                                    onClick={onIncreaseClickHandler}
                                    aria-label="plus"
                                >
                                    <Icon src={Plus} size={10} color="common.ui1" />
                                </Clickable>
                                <Clickable
                                    {...styles.qtyClickable}
                                    disabled={disabled || Number(currentValue) === minQty}
                                    className={`minus ${disabled ? "disabled" : ""}`}
                                    onClick={onDecreaseClickHandler}
                                    aria-label="minus"
                                >
                                    <Icon src={Minus} size={10} color="common.ui1" />
                                </Clickable>
                            </StyledWrapper>
                        )}
                        {/* XNG-Changes: GSS-366: moved Tooltip here */}
                        {showMultipleSaleQty && (
                            <StyledWrapper {...styles.multipleSaleQtyWrapper}>
                                <Tooltip
                                    text={
                                        <StyledWrapper>
                                            <Typography as="p" weight="bold">
                                                {siteMessage(
                                                    "Guest_Multiple_Sale_Qty_Title_Msg",
                                                    "Multiple Sale Quantity",
                                                )}
                                            </Typography>
                                            {siteMessage(
                                                "Guest_Multiple_Sale_Qty_Body_Msg",
                                                "This product must be purchased in case pack increments",
                                            )}
                                        </StyledWrapper>
                                    }
                                    triggerComponent={<Icon {...styles.QuestionIconStyles} src={QuestionTooltip} />}
                                    {...styles.multipleSaleQtyToolTip}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </>
            ) : (
                <>
                    <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
                    {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                    <Typography {...styles.printQtyLabel}>{labelOverride || translate("QTY")}</Typography>
                    <Typography {...styles.valueText}>{currentValue}</Typography>
                </>
            )}
        </StyledWrapper>
    );
};

export default QtyField;
